import Joi from "joi";

const objectIdPattern = /^[0-9a-fA-F]{24}$/;

/**
 * Validation schema for quiz question
 */
const quizQuestionSchema = Joi.object({
  questionText: Joi.string().trim().min(5).max(1000).required().messages({
    "string.min": "Question text must be at least 5 characters",
    "string.max": "Question text cannot exceed 1000 characters",
    "any.required": "Question text is required",
  }),

  questionType: Joi.string()
    .valid("multiple_choice", "true_false", "short_answer", "essay")
    .required()
    .messages({
      "any.only": "Invalid question type",
      "any.required": "Question type is required",
    }),

  options: Joi.array()
    .items(Joi.string().trim().min(1).max(200))
    .min(2)
    .optional()
    .messages({
      "array.min": "Multiple choice questions must have at least 2 options",
    }),

  correctAnswer: Joi.alternatives()
    .try(Joi.string(), Joi.array().items(Joi.string()))
    .optional(),

  marks: Joi.number().min(0).max(100).required().messages({
    "number.min": "Marks cannot be negative",
    "number.max": "Marks cannot exceed 100 per question",
    "any.required": "Marks is required",
  }),

  order: Joi.number().integer().min(0).required().messages({
    "number.min": "Order cannot be negative",
    "any.required": "Order is required",
  }),
});

/**
 * Validation schema for creating a quiz
 */
export const createQuizValidation = Joi.object({
  semesterId: Joi.string().regex(objectIdPattern).required().messages({
    "string.pattern.base": "Invalid semester ID format",
    "any.required": "Semester ID is required",
  }),

  classId: Joi.string().regex(objectIdPattern).required().messages({
    "string.pattern.base": "Invalid class ID format",
    "any.required": "Class ID is required",
  }),

  title: Joi.string().trim().min(3).max(200).required().messages({
    "string.min": "Title must be at least 3 characters",
    "string.max": "Title cannot exceed 200 characters",
    "any.required": "Title is required",
  }),

  description: Joi.string().trim().max(2000).allow("").messages({
    "string.max": "Description cannot exceed 2000 characters",
  }),

  questions: Joi.alternatives()
    .try(Joi.array().items(quizQuestionSchema).min(1), Joi.string())
    .required()
    .messages({
      "array.min": "Quiz must have at least one question",
      "any.required": "Questions are required",
    }),

  duration: Joi.number().integer().min(1).max(300).required().messages({
    "number.min": "Duration must be at least 1 minute",
    "number.max": "Duration cannot exceed 300 minutes (5 hours)",
    "any.required": "Duration is required",
  }),

  deadline: Joi.date().iso().greater("now").required().messages({
    "date.greater": "Deadline must be in the future",
    "any.required": "Deadline is required",
  }),

  startTime: Joi.date().iso().less(Joi.ref("deadline")).messages({
    "date.less": "Start time must be before deadline",
  }),

  shuffleQuestions: Joi.boolean().default(false),

  shuffleOptions: Joi.boolean().default(false),

  showResultsImmediately: Joi.boolean().default(true),

  allowMultipleAttempts: Joi.boolean().default(false),

  maxAttempts: Joi.number().integer().min(1).max(10).optional().messages({
    "number.min": "Max attempts must be at least 1",
    "number.max": "Max attempts cannot exceed 10",
  }),
});

/**
 * Validation schema for updating a quiz
 */
export const updateQuizValidation = Joi.object({
  title: Joi.string().trim().min(3).max(200).messages({
    "string.min": "Title must be at least 3 characters",
    "string.max": "Title cannot exceed 200 characters",
  }),

  description: Joi.string().trim().max(2000).allow("").messages({
    "string.max": "Description cannot exceed 2000 characters",
  }),

  questions: Joi.alternatives()
    .try(Joi.array().items(quizQuestionSchema).min(1), Joi.string())
    .messages({
      "array.min": "Quiz must have at least one question",
    }),

  duration: Joi.number().integer().min(1).max(300).messages({
    "number.min": "Duration must be at least 1 minute",
    "number.max": "Duration cannot exceed 300 minutes (5 hours)",
  }),

  deadline: Joi.date().iso().greater("now").messages({
    "date.greater": "Deadline must be in the future",
  }),

  startTime: Joi.date().iso(),

  shuffleQuestions: Joi.boolean(),

  shuffleOptions: Joi.boolean(),

  showResultsImmediately: Joi.boolean(),

  allowMultipleAttempts: Joi.boolean(),

  maxAttempts: Joi.number().integer().min(1).max(10).messages({
    "number.min": "Max attempts must be at least 1",
    "number.max": "Max attempts cannot exceed 10",
  }),
});

/**
 * Validation schema for submitting quiz answers
 */
export const submitQuizValidation = Joi.object({
  attemptId: Joi.string().regex(objectIdPattern).required().messages({
    "string.pattern.base": "Invalid attempt ID format",
    "any.required": "Attempt ID is required",
  }),

  answers: Joi.array()
    .items(
      Joi.object({
        questionId: Joi.string().required().messages({
          "any.required": "Question ID is required",
        }),
        answer: Joi.alternatives()
          .try(Joi.string(), Joi.array().items(Joi.string()))
          .required()
          .messages({
            "any.required": "Answer is required",
          }),
      }),
    )
    .min(1)
    .required()
    .messages({
      "array.min": "At least one answer is required",
      "any.required": "Answers array is required",
    }),
});

/**
 * Validation schema for manually grading quiz attempt
 */
export const gradeQuizAttemptValidation = Joi.object({
  answers: Joi.array()
    .items(
      Joi.object({
        questionId: Joi.string().required(),
        marksObtained: Joi.number().min(0).required(),
      }),
    )
    .messages({
      "any.required": "Answers with marks are required for manual grading",
    }),

  feedback: Joi.string().trim().max(1000).allow("").messages({
    "string.max": "Feedback cannot exceed 1000 characters",
  }),
});
